import React, { useState, useEffect } from 'react';
import { X, Mail, Key, Shield } from 'lucide-react';
import { adminAPI } from '../services/api';
import CustomSelect from './common/CustomSelect';
import TextInput from './common/TextInput';
import LoadingButton from './common/LoadingButton';
import { Spinner } from './common/LoadingComponents';
import '../styles/Modal.css';

function AddUserModal({ isOpen, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'member',
    authType: 'password', // 'password', 'invite', or 'sso'
    sendInvite: true
  });
  const [loading, setLoading] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(true);
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [error, setError] = useState('');

  // Check SMTP and SSO status when modal opens
  useEffect(() => {
    if (isOpen) {
      checkConfiguration();
    }
  }, [isOpen]);

  const checkConfiguration = async () => {
    setCheckingConfig(true);
    try {
      // Check SMTP status
      const smtpResponse = await adminAPI.getSettings('smtp');
      const smtp = smtpResponse.data.setting_value;
      const isSmtpConfigured = smtp?.enabled && smtp?.host && smtp?.authUser && smtp?.fromEmail;
      setSmtpEnabled(isSmtpConfigured);

      // Check SSO/OAuth status
      const oauthResponse = await adminAPI.getSettings('oauth');
      const oauth = oauthResponse.data.setting_value;
      const isSsoConfigured = oauth?.google?.enabled || oauth?.microsoft?.enabled || oauth?.custom?.enabled;
      setSsoEnabled(isSsoConfigured);

      // Set default auth type based on what's available
      let defaultAuthType = 'password';
      if (isSmtpConfigured) {
        defaultAuthType = 'invite';
      }
      
      setFormData(prev => ({ 
        ...prev, 
        authType: defaultAuthType,
        sendInvite: defaultAuthType === 'invite'
      }));
    } catch (error) {
      console.error('Failed to check configuration:', error);
      setSmtpEnabled(false);
      setSsoEnabled(false);
      setFormData(prev => ({ ...prev, authType: 'password', sendInvite: false }));
    } finally {
      setCheckingConfig(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation - only check password if using password auth type
    if (formData.authType === 'password' && formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      // Build submission data
      const submitData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        sendInvite: formData.authType === 'invite',
        ssoOnly: formData.authType === 'sso'
      };

      // Only include password if using password auth type
      if (formData.authType === 'password') {
        submitData.password = formData.password;
      }

      await onSubmit(submitData);
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'member',
        authType: smtpEnabled ? 'invite' : 'password',
        sendInvite: smtpEnabled
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container modal-large">
        <div className="modal-header">
          <h3>Add New User</h3>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            {checkingConfig ? (
              <div className="loading-state" style={{ padding: 'var(--spacing-lg)' }}>
                <Spinner size="md" />
                <span style={{ marginLeft: 'var(--spacing-sm)' }}>Loading...</span>
              </div>
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <div className="input-with-indicator">
                      <TextInput
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        autoFocus
                      />
                      <span className="required-indicator">*</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <div className="input-with-indicator">
                      <TextInput
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                      <span className="required-indicator">*</span>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-indicator">
                    <TextInput
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                    <span className="required-indicator">*</span>
                  </div>
                </div>

                {/* Auth Type Selection */}
                <div className={`invite-method-section ${loading ? 'disabled' : ''}`}>
                  <label className="section-label">Authentication Method</label>
                  <div className="invite-method-options">
                    {/* Email Invitation Option - only if SMTP enabled */}
                    {smtpEnabled && (
                      <label 
                        className={`invite-method-card ${formData.authType === 'invite' ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                        onClick={() => !loading && setFormData({ ...formData, authType: 'invite', password: '' })}
                      >
                        <input
                          type="radio"
                          name="authType"
                          checked={formData.authType === 'invite'}
                          onChange={() => setFormData({ ...formData, authType: 'invite', password: '' })}
                          disabled={loading}
                        />
                        <Mail size={24} />
                        <div className="invite-method-info">
                          <strong>Send Email Invitation</strong>
                          <span>User will receive an email to set their own password</span>
                        </div>
                      </label>
                    )}
                    
                    {/* Manual Password Option */}
                    <label 
                      className={`invite-method-card ${formData.authType === 'password' ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                      onClick={() => !loading && setFormData({ ...formData, authType: 'password' })}
                    >
                      <input
                        type="radio"
                        name="authType"
                        checked={formData.authType === 'password'}
                        onChange={() => setFormData({ ...formData, authType: 'password' })}
                        disabled={loading}
                      />
                      <Key size={24} />
                      <div className="invite-method-info">
                        <strong>Set Password Manually</strong>
                        <span>You'll need to share the password with the user</span>
                      </div>
                    </label>

                    {/* SSO Only Option - only if SSO enabled */}
                    {ssoEnabled && (
                      <label 
                        className={`invite-method-card ${formData.authType === 'sso' ? 'selected' : ''} ${loading ? 'disabled' : ''}`}
                        onClick={() => !loading && setFormData({ ...formData, authType: 'sso', password: '' })}
                      >
                        <input
                          type="radio"
                          name="authType"
                          checked={formData.authType === 'sso'}
                          onChange={() => setFormData({ ...formData, authType: 'sso', password: '' })}
                          disabled={loading}
                        />
                        <Shield size={24} />
                        <div className="invite-method-info">
                          <strong>SSO Only</strong>
                          <span>User must sign in with Google, Microsoft, or other SSO provider</span>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Password field - only show if using password auth type */}
                {formData.authType === 'password' && (
                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <div className="input-with-indicator">
                      <TextInput
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={8}
                        disabled={loading}
                        showToggle
                      />
                      <span className="required-indicator">*</span>
                    </div>
                    <small className="form-hint">Must be at least 8 characters</small>
                  </div>
                )}

                {/* Info box when sending invite */}
                {formData.authType === 'invite' && (
                  <div className={`info-box ${loading ? 'disabled' : ''}`}>
                    <Mail size={18} />
                    <div>
                      <strong>Email Invitation</strong>
                      <p>An email will be sent to the user with a secure link to set their password and activate their account.</p>
                    </div>
                  </div>
                )}

                {/* Info box for SSO only */}
                {formData.authType === 'sso' && (
                  <div className={`info-box ${loading ? 'disabled' : ''}`}>
                    <Shield size={18} />
                    <div>
                      <strong>SSO Authentication Only</strong>
                      <p>This user will only be able to sign in using a configured SSO provider (Google, Microsoft, etc.). They will not have a password.</p>
                    </div>
                  </div>
                )}

                {/* Warning when no SMTP and no SSO */}
                {!smtpEnabled && !ssoEnabled && (
                  <div className="warning-box">
                    <Key size={18} />
                    <div>
                      <strong>Manual Password Required</strong>
                      <p>Email invitations and SSO are not available. You must set a password manually and share it with the user.</p>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="role">User Role</label>
                  <div className="input-with-indicator">
                    <CustomSelect
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      disabled={loading}
                      options={[
                        { value: 'member', label: 'Member', icon: '👤' },
                        { value: 'admin', label: 'Administrator', icon: '🛡️' }
                      ]}
                    />
                    <span className="required-indicator">*</span>
                  </div>
                  <small className="form-hint">
                    Admins have full application access. Members can only access their household.
                  </small>
                </div>
              </>
            )}
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn-primary"
              loading={loading}
              disabled={checkingConfig}
            >
              {formData.authType === 'invite' ? 'Create & Send Invite' : 'Create User'}
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddUserModal;
